import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ChevronDown } from "lucide-react";
import { BIZ, IMG } from "../../lib/content";
import { MaskLines, EASE } from "../Reveal";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[calc(100vh-5rem)] mt-20 overflow-hidden grain">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.hero})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-dst-ink via-dst-ink/60 to-dst-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-dst-ink/70 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 min-h-[calc(100vh-5rem)] container-dst flex flex-col justify-center pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-12 bg-dst-red" />
          <span className="eyebrow text-white/80">New Braunfels, TX · Ages 10–35</span>
        </motion.div>

        <h1 className="font-oswald font-700 uppercase tracking-tighter leading-[0.85] text-[15vw] sm:text-7xl lg:text-[8.5rem]">
          <MaskLines lines={["Develop", "The Athlete."]} lineClassName="text-white" start={0.15} />
          <span className="block h-2" />
          <MaskLines lines={["Unlock Your", "Potential."]} lineClassName="text-dst-red" start={0.45} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
          className="mt-8 max-w-xl text-white/75 text-base lg:text-lg leading-relaxed"
        >
          Elite group training built on accountability, discipline, and purpose — developing stronger, faster, smarter baseball and softball athletes. Private one-on-one sessions available too.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a href={BIZ.bookUrl} target="_blank" rel="noreferrer" data-testid="hero-book" className="btn-dst bg-dst-red text-white hover:bg-white hover:text-dst-ink group">
            Book Your First Session
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a href="/services" data-testid="hero-programs" className="btn-dst border border-white/40 text-white hover:bg-white hover:text-dst-ink">
            View Programs
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 text-white/50"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
};
